"use client";

import React, { useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Input } from "../ui/input";
import { SectionCard } from "./SectionCard";
import { FieldRow } from "./FieldRow";

const mandatoryMessage = "Your answer is required.";

function getTodayLocalDate() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

const DateInput = React.forwardRef<
  HTMLInputElement,
  { value?: string; onClick?: () => void; disabled?: boolean; "data-invalid"?: "true" | "false" }
>(({ value, onClick, disabled, ...props }, ref) => (
    <div className="relative">
      <Input
        ref={ref}
        name="submission_date_display"
        placeholder="DD / MM / YYYY"
        value={value ?? ""}
        readOnly
        disabled={disabled}
        onClick={onClick}
        className="h-11 rounded-xl pr-12"
        required
        {...props}
      />
      <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        className="absolute inset-y-0 right-2 flex items-center justify-center rounded-lg px-2 text-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
        aria-label="Select submission date"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
          <path
            fill="currentColor"
            d="M7 2a1 1 0 0 1 1 1v1h8V3a1 1 0 1 1 2 0v1h1a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h1V3a1 1 0 0 1 1-1zm12 7H5v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9zM6 6a1 1 0 0 0-1 1v1h14V7a1 1 0 0 0-1-1H6z"
          />
        </svg>
      </button>
    </div>
  ));

DateInput.displayName = "DateInput";

export function SubmissionDateSection({ invalidFields = [] }: { invalidFields?: string[] }) {
  const [selectedDate] = useState<Date>(getTodayLocalDate);
  const isInvalid = invalidFields.includes("submission_date_picker");

  const submissionDate = useMemo(() => {
    if (!selectedDate) return "";
    const paddedDay = String(selectedDate.getDate()).padStart(2, "0");
    const paddedMonth = String(selectedDate.getMonth() + 1).padStart(2, "0");
    return `${selectedDate.getFullYear()}-${paddedMonth}-${paddedDay}`;
  }, [selectedDate]);

  return (
    <SectionCard title="Submission Date *" subtitle="Day / Month / Year">
      <FieldRow label="Date" required error={isInvalid ? mandatoryMessage : undefined}>
        <DatePicker
          selected={selectedDate}
          onChange={() => undefined}
          dateFormat="dd/MM/yyyy"
          placeholderText="DD / MM / YYYY"
          id="submission_date_picker"
          minDate={selectedDate}
          maxDate={selectedDate}
          disabled
          customInput={<DateInput data-invalid={isInvalid ? "true" : "false"} />}
        />
      </FieldRow>
      <input type="hidden" name="submission_date" value={submissionDate} />
    </SectionCard>
  );
}
